/**
 * Utility function to get a valid Cognito token, refreshing if necessary
 * Uses a singleton pattern to prevent multiple simultaneous refresh attempts
 */
let refreshPromise: Promise<string | null> | null = null;
let isRefreshing = false;

export const getValidToken = async (): Promise<string | null> => {
  try {
    // CRITICAL: If a refresh is already in progress, ALWAYS wait for it first
    // This prevents race conditions where multiple components check token validity
    // at the same time before refresh completes
    if (isRefreshing && refreshPromise) {
      console.log('⏳ Token refresh already in progress, waiting for completion...');
      const refreshedToken = await refreshPromise;
      if (refreshedToken) {
        console.log('✅ Got refreshed token from in-progress refresh');
        return refreshedToken;
      }
      // If refresh failed, continue to check current token
      console.log('⚠️ Refresh failed, checking current token...');
    }
    
    let token = localStorage.getItem('cognitoToken') || localStorage.getItem('token');
    
    if (!token) {
      console.log('❌ No token found in localStorage');
      return null;
    }

    // Check if token is expired or expiring soon (within 5 minutes)
    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      const exp = payload.exp * 1000; // Convert to milliseconds
      const now = Date.now();
      const timeUntilExpiry = exp - now;
      
      // Add 60 second buffer to account for clock skew and request time
      const buffer = 60 * 1000; // 60 seconds
      
      // If expired or expiring within 5 minutes + buffer, refresh it
      if (exp < (now + buffer) || timeUntilExpiry < (5 * 60 * 1000 + buffer)) {
        console.log('⚠️ Token expired or expiring soon, attempting to refresh...');
        console.log('🔍 Old token exp:', new Date(exp).toISOString(), 'now:', new Date(now).toISOString(), 'timeUntilExpiry:', Math.floor(timeUntilExpiry / 1000), 'seconds');
        
        // Double-check: If a refresh started while we were checking, wait for it
        if (isRefreshing && refreshPromise) {
          console.log('⏳ Token refresh started while checking, waiting...');
          const refreshedToken = await refreshPromise;
          return refreshedToken || null;
        }
        
        // Start a new refresh
        isRefreshing = true;
        refreshPromise = (async () => {
          try {
            const { fetchAuthSession } = await import('aws-amplify/auth');
            console.log('🔄 Calling fetchAuthSession with forceRefresh...');
            const session = await fetchAuthSession({ forceRefresh: true });
            
            if (session.tokens?.accessToken) {
              const newToken = session.tokens.accessToken.toString();
              console.log('🔍 New token received, length:', newToken.length);
              
              // Parse new token to verify it's different and valid
              try {
                const newPayload = JSON.parse(atob(newToken.split('.')[1]));
                const newExp = newPayload.exp * 1000;
                const newNow = Date.now();
                const timeUntilNewExpiry = newExp - newNow;
                console.log('🔍 New token exp:', new Date(newExp).toISOString(), 'now:', new Date(newNow).toISOString(), 'timeUntilExpiry:', Math.floor(timeUntilNewExpiry / 1000), 'seconds');
                
                if (newExp > (newNow + buffer)) {
                  localStorage.setItem('cognitoToken', newToken);
                  console.log('✅ Token refreshed successfully and saved');
                  return newToken;
                } else {
                  console.error('❌ Refreshed token is also expired or expiring too soon!');
                  return null;
                }
              } catch (parseErr) {
                console.error('❌ Error parsing refreshed token:', parseErr);
                return null;
              }
            } else {
              console.error('❌ Unable to refresh token - no access token in session');
              console.error('❌ Session tokens:', session.tokens);
              return null;
            }
          } catch (refreshError: any) {
            console.error('❌ Token refresh failed:', refreshError);
            console.error('❌ Refresh error details:', refreshError.message);
            if (refreshError.stack) {
              console.error('❌ Stack:', refreshError.stack);
            }
            return null;
          } finally {
            // Clear the refresh state
            isRefreshing = false;
            refreshPromise = null;
          }
        })();
        
        const refreshedToken = await refreshPromise;
        return refreshedToken || null;
      } else {
        console.log('✅ Token is still valid, exp:', new Date(exp).toISOString(), 'timeUntilExpiry:', Math.floor(timeUntilExpiry / 1000), 'seconds');
        return token;
      }
    } catch (parseError) {
      console.error('❌ Error parsing token:', parseError);
      // If we can't parse the token, try to refresh it anyway
      if (isRefreshing && refreshPromise) {
        return await refreshPromise;
      }
      
      isRefreshing = true;
      refreshPromise = (async () => {
        try {
          const { fetchAuthSession } = await import('aws-amplify/auth');
          const session = await fetchAuthSession({ forceRefresh: true });
          if (session.tokens?.accessToken) {
            const newToken = session.tokens.accessToken.toString();
            localStorage.setItem('cognitoToken', newToken);
            console.log('✅ Token refreshed successfully');
            return newToken;
          } else {
            return null;
          }
        } catch (refreshError) {
          console.error('❌ Token refresh failed:', refreshError);
          return null;
        } finally {
          isRefreshing = false;
          refreshPromise = null;
        }
      })();
      
      return await refreshPromise;
    }
  } catch (error) {
    console.error('❌ Error getting valid token:', error);
    return null;
  }
};

